import {
    BarChart, Bar, XAxis, YAxis, Tooltip, PieChart, Pie, Cell,
    ResponsiveContainer, CartesianGrid, LabelList, Legend, LineChart, Line
} from 'recharts';
import PropTypes from 'prop-types';

// Constants
const COLORS = ['#8a2432', '#dedfdf', '#c1c1c1', '#d26574'];
const RADIAN = Math.PI / 180;

// Custom Components
const CustomPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
        <text
            x={x}
            y={y}
            fill="white"
            textAnchor={x > cx ? 'start' : 'end'}
            dominantBaseline="central"
            fontSize={12}
            fontWeight="bold"
        >
            {`${(percent * 100).toFixed(0)}%`}
        </text>
    );
};

const CustomBarLabel = ({ x, y, value }) => (
    <text x={x} y={y - 10} fill="#000" textAnchor="middle" fontSize={14}>
        {value} €
    </text>
);

const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
        return (
            <div className="bg-white p-2 border border-[#F2C3CA] rounded-lg shadow-md">
                <p className="text-[#8B2433] font-medium">{payload[0].name}</p>
                <p className="text-[#8B2433]">{payload[0].value} €</p>
            </div>
        );
    }
    return null;
};

// Chart Components
const BarChartCard = ({ title, data }) => {
    const hasNoData = !data || data.length === 0 || data.every(item => item.value === 0);
    if (hasNoData) {
        return (
            <div className='w-full h-[400px] lg:h-auto lg:w-1/2 bg-[#f7f6f6] rounded-2xl p-4 flex flex-col items-center justify-center'>
                <h1 className='mb-4 text-[#8B2433] poppins-medium text-lg'>{title}</h1>
                <p className='text-[#8B2433]'>No data available</p>
            </div>
        );
    }

    // Calculate max value in data (rounded up to nearest 10 for a clean scale)
    const maxValue = Math.max(...data.map(item => item.value));
    const roundedMax = Math.ceil(maxValue / 10) * 10; // Round up to nearest 10

    // Generate dynamic ticks (e.g., [0, 20, 40, ...] or [0, 25, 50, ...])
    const tickCount = 6; // Adjust based on how many ticks you want
    const tickInterval = Math.ceil(roundedMax / (tickCount - 1) / 10) * 10; // Round to nearest 10
    const ticks = Array.from({ length: tickCount }, (_, i) => i * tickInterval);

    // Define the color palette
    const colorPalette = ['#d26574', '#aaaaaa', '#8a2432', '#cecfcf'];

    return (
        <div className='w-full h-[400px] lg:h-auto lg:w-1/2 bg-[#f7f6f6] rounded-2xl p-4'>
            <h1 className='mb-4 text-[#8B2433] poppins-medium text-lg'>{title}</h1>
            <ResponsiveContainer width="100%" height="100%">
                <BarChart
                    data={data}
                    margin={{ top: 20, bottom: 35 }}
                    barSize={30}
                >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis
                        dataKey="name"
                        angle={-25}
                        textAnchor="end"
                        height={60}
                        tick={{ fontSize: 12, fill: '#8B2433' }}
                    />
                    <YAxis
                        domain={[0, roundedMax]}
                        ticks={ticks}
                        tick={{ fill: '#8B2433' }}
                    />
                    <Tooltip
                        formatter={(value) => [`${value} €`, "Ricavi"]}
                        contentStyle={{
                            backgroundColor: '#fff',
                            borderColor: '#F2C3CA',
                            borderRadius: '8px',
                            color: '#8B2433'
                        }}
                    />
                    <Bar dataKey="value" name="Ricavi">
                        {data.map((entry, index) => (
                            <Cell
                                key={`cell-${index}`}
                                fill={colorPalette[index % colorPalette.length]}
                            />
                        ))
                        }
                        <LabelList
                            dataKey="value"
                            position="top"
                            formatter={(value) => `${value}€`}
                            fill="#8B2433"
                            fontSize={12}
                        />
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
};

const LineChartCard = ({ title, data }) => {
    // Calculate max value in data (rounded up to nearest 10 for a clean scale)
    const maxValue = Math.max(...data.map(item => item.value));
    const roundedMax = Math.ceil(maxValue / 10) * 10; // Round up to nearest 10

    // Generate dynamic ticks (e.g., [0, 20, 40, ...] or [0, 25, 50, ...])
    const tickCount = 6; // Adjust based on how many ticks you want
    const tickInterval = Math.ceil(roundedMax / (tickCount - 1) / 10) * 10; // Round to nearest 10
    const ticks = Array.from({ length: tickCount }, (_, i) => i * tickInterval);

    return (
        <div className='w-full lg:w-1/2 h-[400px] lg:h-auto  bg-[#f7f6f6] rounded-2xl p-4'>
            <h1 className='mb-4 text-[#8B2433] poppins-medium text-lg'>{title}</h1>
            <ResponsiveContainer width="100%" height={300}>
                <LineChart
                    data={data}
                    margin={{ top: 30, right: 20, left: 10, bottom: 40 }}
                >
                    <CartesianGrid stroke="#e0e0e0" />
                    <XAxis
                        dataKey="name"
                        angle={-35}
                        textAnchor="end"
                        tick={{ fill: '#8B2433', fontSize: 12 }}
                        interval={0}
                        height={60}
                    />
                    <YAxis
                        domain={[0, roundedMax]} // Dynamic max
                        ticks={ticks} // Dynamic ticks
                        tick={{ fill: '#8B2433', fontSize: 12 }}
                    />
                    <Tooltip
                        formatter={(value) => [`${value} €`, "Ricavi"]}
                        contentStyle={{
                            backgroundColor: '#fff',
                            borderColor: '#F2C3CA',
                            borderRadius: '8px',
                            color: '#8B2433',
                        }}
                    />
                    <Line
                        type="monotone"
                        dataKey="value"
                        stroke="#000"
                        dot={{ fill: '#8a2432', r: 6 }}
                        strokeWidth={2}
                    >
                        <LabelList content={CustomBarLabel} />
                    </Line>
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
};

const PieChartCard = ({ data, title }) => {
    const hasNoData = !data || data.length === 0 || data.every(item => item.value === 0);
    if (hasNoData) {
        return (
            <div className='w-full h-[400px] lg:h-auto lg:w-1/2 bg-[#f7f6f6] rounded-2xl p-4 flex flex-col items-center justify-center'>
                <h1 className='mb-4 text-[#8B2433] poppins-medium text-lg'>{title}</h1>
                <p className='text-[#8B2433]'>No data available</p>
            </div>
        );
    }

    return (
        <div className='w-full lg:h-auto lg:w-1/2 bg-[#f7f6f6] rounded-2xl p-4'>
            <h1 className='mb-4 text-[#8B2433] poppins-medium text-lg'>{title}</h1>
            <ResponsiveContainer width="100%" height={400}>
                <PieChart>
                    <Pie
                        data={data}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                        label={CustomPieLabel}
                    >
                        {data.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                    </Pie>
                    <Legend
                        layout="vertical"
                        verticalAlign="middle"
                        align="right"
                        formatter={(value, entry, index) => (
                            <span className="text-[#8B2433] poppins-regular">
                                {value}: {data[index].value} €
                            </span>
                        )}
                    />
                    <Tooltip content={<CustomTooltip />} />
                </PieChart>
            </ResponsiveContainer>
        </div>
    );
};

const NumberChartCard = ({ title, data }) => {
    const hasNoData = !data || data.length === 0 || data.every(item => item.value === 0);
    if (hasNoData) {
        return (
            <div className='w-full h-[400px] lg:h-auto lg:w-1/2 bg-[#f7f6f6] rounded-2xl p-4 flex flex-col items-center justify-center'>
                <h1 className='mb-4 text-[#8B2433] poppins-medium text-lg'>{title}</h1>
                <p className='text-[#8B2433]'>No data available</p>
            </div>
        );
    }

    return (
        <div className='w-full lg:w-1/2'>
            <h1 className='mb-4 text-[#8B2433] poppins-medium text-lg'>{title}</h1>
            <div className='w-full grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-3 lg:min-h-[250px] gap-4 bg-[#f7f6f6] rounded-2xl p-4'>
                {data.map((item, index) => (
                    <div key={index} className='gap-3'>
                        <div className='w-full bg-[#d26574] h-[100px] rounded-xl p-[20px]'>
                            <p className='text-[#fff] text-[13px]'>{item?.name}</p>
                            <div className='text-[20px] font-medium text-[#fff] pt-1 border border-x-0 border-b-0 mt-1 border-t-1'>
                                {item?.value} €
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

// Main Component
const VATChart = (props) => {
    const { chartType, wineTypeData, servingMethodData } = props;

    const renderChart = () => {
        switch (chartType) {
            case 'Grafico Lineare':
                return (
                    <div className="w-full flex gap-10 flex-wrap lg:flex-nowrap ">
                        <LineChartCard title="Tipologia" data={wineTypeData} />
                        <LineChartCard title="Servizio" data={servingMethodData} />
                    </div>
                );
            case 'Grafico a Torta':
                return (
                    <div className="w-full flex gap-10 flex-wrap lg:flex-nowrap graph-wrapper">
                        <PieChartCard title="Tipologia" data={wineTypeData} />
                        <PieChartCard title="Servizio" data={servingMethodData} />
                    </div>
                );
            case 'Numerico':
                return (
                    <div className="w-full flex justify-between gap-10 flex-wrap lg:flex-nowrap">
                        <NumberChartCard title="Tipologia" data={wineTypeData} />
                        <NumberChartCard title="Servizio" data={servingMethodData} />
                    </div>
                );
            default:
                return (
                    <div className="w-full h-auto lg:h-[400px] flex gap-10 flex-wrap lg:flex-nowrap">
                        <BarChartCard title="Tipologia" data={wineTypeData} />
                        <BarChartCard title="Servizio" data={servingMethodData} />
                    </div>
                );
        }
    };

    return (
        <div className='border shadow-xl rounded-3xl poppins-medium p-8 bg-white'>
            <h1 className='poppins-regular text-2xl text-[#8B2433] mb-6'>
                {/* Total revenue excluding VAT */}
                Ricavi
            </h1>
            {renderChart()}
        </div>
    );
};

CustomPieLabel.propTypes = {
    cx: PropTypes.number.isRequired,
    cy: PropTypes.number.isRequired,
    midAngle: PropTypes.number.isRequired,
    innerRadius: PropTypes.number.isRequired,
    outerRadius: PropTypes.number.isRequired,
    percent: PropTypes.number.isRequired,
};

CustomBarLabel.propTypes = {
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

CustomTooltip.propTypes = {
    active: PropTypes.bool,
    payload: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string,
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        })
    ),
};

BarChartCard.propTypes = {
    title: PropTypes.string.isRequired,
    data: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
            value: PropTypes.number.isRequired,
        })
    ).isRequired,
};

LineChartCard.propTypes = {
    title: PropTypes.string.isRequired,
    data: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
            value: PropTypes.number.isRequired,
        })
    ).isRequired,
};

PieChartCard.propTypes = {
    title: PropTypes.string.isRequired,
    data: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
            value: PropTypes.number.isRequired,
        })
    ).isRequired,
};

NumberChartCard.propTypes = {
    title: PropTypes.string.isRequired,
    data: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
            value: PropTypes.number.isRequired,
        })
    ).isRequired,
};

VATChart.propTypes = {
    chartType: PropTypes.string.isRequired,
    wineTypeData: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
            value: PropTypes.number.isRequired,
        })
    ).isRequired,
    servingMethodData: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
            value: PropTypes.number.isRequired,
        })
    ).isRequired,
};

export default VATChart;